/**
 * Corpus **scope** picker for the AI Chat composer.
 *
 * Unified list of buckets + folders + docs from My Corpus. Users click a
 * chevron to expand a bucket/folder and drill down, and click a separate
 * "Include" action to add the whole bucket, folder, or a specific doc to
 * the chat thread's Corpus search scope. Empty scope = search the whole
 * workspace. See mem://features/corpus-rag.md.
 *
 * Two-pass Primary-authority retrieval always applies, so Primary Corpus
 * stays canonical whether or not the user scopes.
 */
import { useEffect, useMemo, useState } from "react";
import {
  BookText,
  Check,
  ChevronRight,
  Folder,
  FolderOpen,
  LayoutGrid,
  ListFilter,
  Plus,
  X,
  Layers,
} from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Toggle } from "@/components/ui/toggle";
import {
  listDocuments,
  listFolders,
  BUCKET_LABELS,
  BUCKET_ORDER,
  type CorpusBucket,
  type CorpusDocumentRow,
  type CorpusFolder,
} from "@/lib/workspaceCorpus";
import { MAX_SCOPED_REFS, type ThreadScope } from "@/lib/pieceCorpusScope";

type PickKind = "doc" | "folder" | "bucket";
export type CorpusPickerPickHandler = (
  kind: PickKind,
  id: string,
  label: string,
) => void;

type Props = {
  workspaceId: string;
  scope: ThreadScope;
  onChange: (next: ThreadScope) => void;
  disabled?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
  hideChips?: boolean;
  /**
   * When provided, clicking an item does NOT toggle the persistent scope.
   * Instead we call `onPick(kind, id, label)` and close the popover — the
   * caller uses this to insert an inline `@Label` mention into the composer
   * as a per-message pin.
   */
  onPick?: CorpusPickerPickHandler;
};

type LoadedData = {
  folders: CorpusFolder[];
  docs: CorpusDocumentRow[];
};

const FOLDER_PATH_SEP = " / ";

function buildFolderPath(f: CorpusFolder, byId: Map<string, CorpusFolder>): string {
  const parts: string[] = [];
  let cur: CorpusFolder | undefined = f;
  const seen = new Set<string>();
  while (cur && !seen.has(cur.id)) {
    parts.unshift(cur.name);
    seen.add(cur.id);
    cur = cur.parent_folder_id ? byId.get(cur.parent_folder_id) : undefined;
  }
  return parts.join(FOLDER_PATH_SEP);
}

export const CorpusPickerPopover = ({
  workspaceId,
  scope,
  onChange,
  disabled,
  open: openProp,
  onOpenChange,
  hideTrigger,
  hideChips,
  onPick,
}: Props) => {
  const [openInternal, setOpenInternal] = useState(false);
  const open = openProp ?? openInternal;
  const setOpen = (next: boolean) => {
    if (openProp === undefined) setOpenInternal(next);
    onOpenChange?.(next);
  };
  const [data, setData] = useState<LoadedData | null>(null);
  const [groupByFolder, setGroupByFolder] = useState(true);

  useEffect(() => {
    if (!open || data !== null) return;
    let cancelled = false;
    void (async () => {
      try {
        const [folders, docs] = await Promise.all([
          listFolders(workspaceId),
          listDocuments(workspaceId),
        ]);
        if (!cancelled) setData({ folders, docs });
      } catch {
        if (!cancelled) setData({ folders: [], docs: [] });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, workspaceId, data]);

  const folderById = useMemo(() => {
    const m = new Map<string, CorpusFolder>();
    for (const f of data?.folders ?? []) m.set(f.id, f);
    return m;
  }, [data]);

  const docsByFolder = useMemo(() => {
    const m = new Map<string | null, CorpusDocumentRow[]>();
    for (const d of data?.docs ?? []) {
      const key = d.folder_id ?? null;
      const arr = m.get(key) ?? [];
      arr.push(d);
      m.set(key, arr);
    }
    return m;
  }, [data]);

  const docById = useMemo(() => {
    const m = new Map<string, CorpusDocumentRow>();
    for (const d of data?.docs ?? []) m.set(d.id, d);
    return m;
  }, [data]);

  // Docs whose folder's bucket matches a given bucket (used for bucket
  // counts and for showing unfiled docs directly under a bucket row).
  const docsInBucket = useMemo(() => {
    const m = new Map<CorpusBucket, CorpusDocumentRow[]>();
    for (const b of BUCKET_ORDER) m.set(b, []);
    for (const d of data?.docs ?? []) {
      const folder = d.folder_id ? folderById.get(d.folder_id) : null;
      // Unfiled docs (no folder) default to "secondary" bucket (matches RPC default).
      const bucket: CorpusBucket = folder?.bucket ?? "secondary";
      m.get(bucket)!.push(d);
    }
    return m;
  }, [data, folderById]);

  const scopedDocs = new Set(scope.documentIds);
  const scopedFolders = new Set(scope.folderIds);
  const scopedBuckets = new Set(scope.buckets);
  const totalRefs = scopedDocs.size + scopedFolders.size + scopedBuckets.size;
  const atCap = totalRefs >= MAX_SCOPED_REFS;

  const toggleDoc = (id: string) => {
    if (onPick) {
      const d = docById.get(id);
      onPick("doc", id, d?.title || "Untitled");
      setOpen(false);
      return;
    }
    const next = new Set(scope.documentIds);
    if (next.has(id)) next.delete(id);
    else if (!atCap) next.add(id);
    else return;
    onChange({
      documentIds: Array.from(next),
      folderIds: scope.folderIds,
      buckets: scope.buckets,
    });
  };

  const toggleFolder = (id: string) => {
    if (onPick) {
      const f = folderById.get(id);
      onPick("folder", id, f ? buildFolderPath(f, folderById) : "Folder");
      setOpen(false);
      return;
    }
    const next = new Set(scope.folderIds);
    if (next.has(id)) next.delete(id);
    else if (!atCap) next.add(id);
    else return;
    onChange({
      documentIds: scope.documentIds,
      folderIds: Array.from(next),
      buckets: scope.buckets,
    });
  };

  const toggleBucket = (b: CorpusBucket) => {
    if (onPick) {
      onPick("bucket", b, BUCKET_LABELS[b]);
      setOpen(false);
      return;
    }
    const next = new Set(scope.buckets);
    if (next.has(b)) next.delete(b);
    else if (!atCap) next.add(b);
    else return;
    onChange({
      documentIds: scope.documentIds,
      folderIds: scope.folderIds,
      buckets: Array.from(next),
    });
  };

  return (
    <div className="flex flex-wrap items-center gap-1">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          {hideTrigger ? (
            <span className="sr-only" aria-hidden />
          ) : (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={disabled}
              className="h-6 gap-1 px-2 text-[11px] font-normal"
              title="Scope this chat's Corpus search to buckets, folders, or specific docs (type / in the composer for a shortcut)"
            >
              <Plus className="h-3 w-3" />
              <BookText className="h-3 w-3" />
              Scope
              {totalRefs > 0 ? (
                <span className="ml-0.5 rounded-sm bg-primary/15 px-1 text-[10px] text-primary">
                  {totalRefs}
                </span>
              ) : null}
            </Button>
          )}
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[420px] p-0">
          <div className="flex items-center justify-between border-b border-border px-2 py-1.5">
            <span className="text-[11px] font-medium text-muted-foreground">
              Include a bucket, folder, or doc in this chat's Corpus search
            </span>
            <Toggle
              size="sm"
              pressed={groupByFolder}
              onPressedChange={setGroupByFolder}
              aria-label={groupByFolder ? "Switch to flat list" : "Group by folder"}
              className="h-6 px-1.5 text-[10px]"
              title={groupByFolder ? "Switch to flat list" : "Group by folder"}
            >
              {groupByFolder ? <LayoutGrid className="h-3 w-3" /> : <ListFilter className="h-3 w-3" />}
              <span className="ml-1">{groupByFolder ? "Grouped" : "Flat"}</span>
            </Toggle>
          </div>
          <Command>
            <CommandInput placeholder="Search folders and docs…" />
            <CommandList className="max-h-[380px]">
              <CommandEmpty>
                {data === null ? "Loading…" : "No Corpus content in this workspace yet."}
              </CommandEmpty>

              {groupByFolder ? (
                <div className="py-1">
                  {BUCKET_ORDER.map((bucket) => {
                    const bucketFolders = (data?.folders ?? []).filter(
                      (f) => f.bucket === bucket,
                    );
                    const roots = bucketFolders.filter((f) => f.parent_folder_id === null);
                    const bucketDocs = docsInBucket.get(bucket) ?? [];
                    // Unfiled docs shown directly under the bucket row when expanded.
                    const unfiledDocs = (docsByFolder.get(null) ?? []).filter(
                      () => bucket === "secondary", // orphans default to secondary bucket in RPC
                    );

                    if (
                      roots.length === 0 &&
                      bucketDocs.length === 0 &&
                      !(bucket === "secondary" && unfiledDocs.length > 0)
                    ) {
                      // Still render the bucket so users can scope the whole
                      // (currently empty) bucket if they want; but skip only
                      // if the entire bucket has no folders AND no docs.
                      if (bucketDocs.length === 0 && roots.length === 0) {
                        // Render an empty-state bucket row so the label
                        // and the "include entire bucket" action are still visible.
                      }
                    }

                    return (
                      <BucketRow
                        key={bucket}
                        bucket={bucket}
                        roots={roots}
                        allFoldersInBucket={bucketFolders}
                        docsByFolder={docsByFolder}
                        unfiledDocs={bucket === "secondary" ? unfiledDocs : []}
                        docCount={bucketDocs.length}
                        folderCount={bucketFolders.length}
                        picked={scopedBuckets.has(bucket)}
                        atCap={atCap}
                        scopedDocs={scopedDocs}
                        scopedFolders={scopedFolders}
                        onToggleBucket={toggleBucket}
                        onToggleFolder={toggleFolder}
                        onToggleDoc={toggleDoc}
                      />
                    );
                  })}
                </div>
              ) : (
                <CommandGroup>
                  {(data?.folders ?? []).map((f) => {
                    const picked = scopedFolders.has(f.id);
                    const capReached = atCap && !picked;
                    const path = buildFolderPath(f, folderById);
                    return (
                      <CommandItem
                        key={`folder-${f.id}`}
                        value={`folder ${path}`}
                        onSelect={() => !capReached && toggleFolder(f.id)}
                        disabled={capReached}
                        className={`flex items-center justify-between gap-2 ${picked ? "bg-primary/5" : ""}`}
                      >
                        <span className="flex min-w-0 items-center gap-1.5 truncate">
                          <Folder className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                          <span className="truncate">{path}</span>
                        </span>
                        {picked ? (
                          <span className="flex items-center gap-1 text-[10px] text-primary">
                            <Check className="h-3 w-3" /> included
                          </span>
                        ) : null}
                      </CommandItem>
                    );
                  })}
                  {(data?.docs ?? []).map((d) => {
                    const picked = scopedDocs.has(d.id);
                    const capReached = atCap && !picked;
                    const folder = d.folder_id ? folderById.get(d.folder_id) : null;
                    const path = folder ? buildFolderPath(folder, folderById) : "Unfiled";
                    return (
                      <CommandItem
                        key={`doc-${d.id}`}
                        value={`doc ${d.title} ${path}`}
                        onSelect={() => !capReached && toggleDoc(d.id)}
                        disabled={capReached}
                        className={`flex items-center justify-between gap-2 ${picked ? "bg-primary/5" : ""}`}
                      >
                        <span className="flex min-w-0 flex-col">
                          <span className="truncate text-sm">{d.title || "(untitled)"}</span>
                          <span className="truncate text-[10px] text-muted-foreground">{path}</span>
                        </span>
                        {picked ? (
                          <span className="flex items-center gap-1 text-[10px] text-primary">
                            <Check className="h-3 w-3" /> included
                          </span>
                        ) : null}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
          <div className="border-t border-border px-2 py-1.5 text-[10px] text-muted-foreground">
            {totalRefs === 0
              ? "Nothing scoped — search runs across the whole workspace."
              : `${totalRefs} reference${totalRefs === 1 ? "" : "s"} scoped · Primary Corpus is always canonical.`}
          </div>
        </PopoverContent>
      </Popover>

      {hideChips ? null : (
        <>
          {scope.buckets.map((b) => (
            <Badge key={`b-${b}`} variant="secondary" className="h-5 gap-1 px-1.5 text-[10px]">
              <Layers className="h-3 w-3" />
              <span className="max-w-[140px] truncate">{BUCKET_LABELS[b]}</span>
              <button
                type="button"
                onClick={() => toggleBucket(b)}
                className="text-muted-foreground hover:text-foreground"
                aria-label={`Remove ${BUCKET_LABELS[b]} bucket from scope`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          {scope.folderIds.map((id) => {
            const f = folderById.get(id);
            const label = f ? buildFolderPath(f, folderById) : id.slice(0, 6);
            return (
              <Badge key={`f-${id}`} variant="secondary" className="h-5 gap-1 px-1.5 text-[10px]">
                <Folder className="h-3 w-3" />
                <span className="max-w-[140px] truncate">{label}</span>
                <button
                  type="button"
                  onClick={() => toggleFolder(id)}
                  className="text-muted-foreground hover:text-foreground"
                  aria-label={`Remove folder ${label} from scope`}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            );
          })}
          {scope.documentIds.map((id) => {
            const d = docById.get(id);
            const label = d?.title || id.slice(0, 6);
            return (
              <Badge key={`d-${id}`} variant="secondary" className="h-5 gap-1 px-1.5 text-[10px]">
                <span className="max-w-[140px] truncate">{label}</span>
                <button
                  type="button"
                  onClick={() => toggleDoc(id)}
                  className="text-muted-foreground hover:text-foreground"
                  aria-label={`Remove ${label} from scope`}
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            );
          })}
        </>
      )}
    </div>
  );
};

// -------------------- Row primitives --------------------

/**
 * Renders one Corpus bucket (Primary / Secondary / Supplemental) as a
 * top-level, expandable, includable row. Includes all descendant folders
 * and unfiled docs when expanded.
 */
const BucketRow = ({
  bucket,
  roots,
  allFoldersInBucket,
  docsByFolder,
  unfiledDocs,
  docCount,
  folderCount,
  picked,
  atCap,
  scopedDocs,
  scopedFolders,
  onToggleBucket,
  onToggleFolder,
  onToggleDoc,
}: {
  bucket: CorpusBucket;
  roots: CorpusFolder[];
  allFoldersInBucket: CorpusFolder[];
  docsByFolder: Map<string | null, CorpusDocumentRow[]>;
  unfiledDocs: CorpusDocumentRow[];
  docCount: number;
  folderCount: number;
  picked: boolean;
  atCap: boolean;
  scopedDocs: Set<string>;
  scopedFolders: Set<string>;
  onToggleBucket: (b: CorpusBucket) => void;
  onToggleFolder: (id: string) => void;
  onToggleDoc: (id: string) => void;
}) => {
  const [expanded, setExpanded] = useState(bucket === "primary");
  const capReached = atCap && !picked;

  return (
    <div className={`mb-0.5 rounded-sm ${picked ? "bg-primary/5" : ""}`}>
      <div className="flex items-center gap-1 px-1 py-1">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="rounded p-0.5 text-muted-foreground hover:text-foreground"
          aria-label={expanded ? `Collapse ${BUCKET_LABELS[bucket]}` : `Expand ${BUCKET_LABELS[bucket]}`}
        >
          <ChevronRight
            className={`h-3.5 w-3.5 transition-transform ${expanded ? "rotate-90" : ""}`}
          />
        </button>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex flex-1 items-center gap-1.5 truncate text-left"
          title={expanded ? "Click to collapse" : "Click to expand"}
        >
          <Layers className="h-3.5 w-3.5 shrink-0 text-primary/80" />
          <span className="truncate text-sm font-semibold">{BUCKET_LABELS[bucket]}</span>
          <span className="text-[10px] text-muted-foreground">
            ({folderCount} folder{folderCount === 1 ? "" : "s"} · {docCount} doc{docCount === 1 ? "" : "s"})
          </span>
        </button>
        <IncludeAction
          picked={picked}
          disabled={capReached}
          onToggle={() => onToggleBucket(bucket)}
          includeLabel={`Include all of ${BUCKET_LABELS[bucket]} in scope`}
          removeLabel={`Remove ${BUCKET_LABELS[bucket]} from scope`}
        />
      </div>
      {expanded ? (
        <div className="pb-1">
          {roots.length === 0 && unfiledDocs.length === 0 ? (
            <div className="px-2 py-1 pl-8 text-[10px] italic text-muted-foreground">
              No folders or docs in this bucket yet.
            </div>
          ) : null}
          {roots.map((f) => (
            <FolderTree
              key={f.id}
              folder={f}
              allFolders={allFoldersInBucket}
              docsByFolder={docsByFolder}
              scopedDocs={scopedDocs}
              scopedFolders={scopedFolders}
              onToggleFolder={onToggleFolder}
              onToggleDoc={onToggleDoc}
              atCap={atCap}
              depth={1}
            />
          ))}
          {unfiledDocs.length > 0 ? (
            <>
              <div className="px-2 py-0.5 pl-8 text-[10px] uppercase tracking-wide text-muted-foreground">
                Unfiled
              </div>
              {unfiledDocs.map((d) => (
                <DocRow
                  key={d.id}
                  doc={d}
                  picked={scopedDocs.has(d.id)}
                  atCap={atCap}
                  onToggle={onToggleDoc}
                  depth={1}
                />
              ))}
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const FolderTree = ({
  folder,
  allFolders,
  docsByFolder,
  scopedDocs,
  scopedFolders,
  onToggleFolder,
  onToggleDoc,
  atCap,
  depth = 0,
}: {
  folder: CorpusFolder;
  allFolders: CorpusFolder[];
  docsByFolder: Map<string | null, CorpusDocumentRow[]>;
  scopedDocs: Set<string>;
  scopedFolders: Set<string>;
  onToggleFolder: (id: string) => void;
  onToggleDoc: (id: string) => void;
  atCap: boolean;
  depth?: number;
}) => {
  const [expanded, setExpanded] = useState(depth < 2);
  const children = allFolders.filter((f) => f.parent_folder_id === folder.id);
  const docs = docsByFolder.get(folder.id) ?? [];
  const totalItems = children.length + docs.length;
  const picked = scopedFolders.has(folder.id);
  const capReached = atCap && !picked;
  const indentPad = { paddingLeft: `${depth * 12 + 6}px` };

  return (
    <div className={picked ? "bg-primary/5" : ""}>
      <div
        className="flex items-center gap-1 px-1 py-0.5"
        style={indentPad}
      >
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="rounded p-0.5 text-muted-foreground hover:text-foreground"
          aria-label={expanded ? "Collapse folder" : "Expand folder"}
        >
          <ChevronRight
            className={`h-3 w-3 transition-transform ${expanded ? "rotate-90" : ""}`}
          />
        </button>
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="flex flex-1 items-center gap-1.5 truncate text-left text-sm"
          title={expanded ? "Click to collapse" : "Click to expand"}
        >
          {expanded ? (
            <FolderOpen className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          ) : (
            <Folder className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          )}
          <span className="truncate">{folder.name}</span>
          <span className="text-[10px] text-muted-foreground">({totalItems})</span>
        </button>
        <IncludeAction
          picked={picked}
          disabled={capReached}
          onToggle={() => onToggleFolder(folder.id)}
          includeLabel={`Include folder ${folder.name} in scope`}
          removeLabel={`Remove folder ${folder.name} from scope`}
        />
      </div>
      {expanded ? (
        <div>
          {children.map((c) => (
            <FolderTree
              key={c.id}
              folder={c}
              allFolders={allFolders}
              docsByFolder={docsByFolder}
              scopedDocs={scopedDocs}
              scopedFolders={scopedFolders}
              onToggleFolder={onToggleFolder}
              onToggleDoc={onToggleDoc}
              atCap={atCap}
              depth={depth + 1}
            />
          ))}
          {docs.map((d) => (
            <DocRow
              key={d.id}
              doc={d}
              picked={scopedDocs.has(d.id)}
              atCap={atCap}
              onToggle={onToggleDoc}
              depth={depth + 1}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
};

const DocRow = ({
  doc,
  picked,
  atCap,
  onToggle,
  depth = 0,
}: {
  doc: CorpusDocumentRow;
  picked: boolean;
  atCap: boolean;
  onToggle: (id: string) => void;
  depth?: number;
}) => {
  const capReached = atCap && !picked;
  return (
    <div
      className={`flex items-center gap-1 px-1 py-0.5 ${picked ? "bg-primary/5" : ""}`}
      style={{ paddingLeft: `${depth * 12 + 22}px` }}
    >
      <BookText className="h-3 w-3 shrink-0 text-muted-foreground" />
      <span className="flex-1 truncate text-sm">{doc.title || "(untitled)"}</span>
      <IncludeAction
        picked={picked}
        disabled={capReached}
        onToggle={() => onToggle(doc.id)}
        includeLabel={`Include doc ${doc.title || "(untitled)"} in scope`}
        removeLabel={`Remove doc ${doc.title || "(untitled)"} from scope`}
      />
    </div>
  );
};

/**
 * The one-and-only "include this row in scope" affordance. Always visible
 * so users don't have to hover to discover it; shows a check + "Included"
 * when the row is already scoped.
 */
const IncludeAction = ({
  picked,
  disabled,
  onToggle,
  includeLabel,
  removeLabel,
}: {
  picked: boolean;
  disabled: boolean;
  onToggle: () => void;
  includeLabel: string;
  removeLabel: string;
}) => (
  <button
    type="button"
    disabled={disabled}
    onClick={(e) => {
      e.stopPropagation();
      onToggle();
    }}
    className={`ml-1 flex shrink-0 items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] transition-colors ${
      picked
        ? "border-primary/40 bg-primary/10 text-primary hover:bg-primary/15"
        : "border-border/60 text-muted-foreground hover:border-primary/40 hover:text-primary"
    } ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
    title={picked ? removeLabel : includeLabel}
  >
    {picked ? (
      <>
        <Check className="h-3 w-3" />
        Included
      </>
    ) : (
      <>
        <Plus className="h-3 w-3" />
        Include
      </>
    )}
  </button>
);

export default CorpusPickerPopover;
